import type { AIActionKey, EntitlementDecision, SkillTier } from '@tabbio/types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface SkillRunEntitlementView {
  kind: 'loading' | 'allowance' | 'included' | 'blocked';
  title: string;
  summary: string;
  detail: string | null;
  canRun: boolean;
  showUpgrade: boolean;
}

/** Keep the client preflight on the same action key selected by SkillsService. */
export function getSkillRunActionKey(tier: SkillTier | string | null | undefined): AIActionKey {
  return tier === 'plus' || tier === 'pro' ? 'individual.skill_run_premium' : 'individual.skill_run';
}

function formatResetAt(resetAt: string, locale: string) {
  const date = new Date(resetAt);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Build customer-facing Skill usage copy from the subscription decision.
 * Internal weighted credits are deliberately excluded: users see runs only.
 */
export function buildSkillRunEntitlementView(params: { decision: EntitlementDecision; locale: string; t: Translate; subscriptionError?: Error | null }): SkillRunEntitlementView {
  const { decision, locale, t, subscriptionError } = params;

  if (decision.reasonCode === 'loading') {
    if (subscriptionError) {
      return {
        kind: 'included',
        title: t('skills.runDialog.allowanceTitle'),
        summary: t('skills.runDialog.allowanceUnavailable'),
        detail: null,
        // The API remains the authoritative gate when the preflight request
        // cannot load; its structured denial is handled inline by the form.
        canRun: true,
        showUpgrade: false,
      };
    }

    return {
      kind: 'loading',
      title: t('skills.runDialog.allowanceTitle'),
      summary: t('skills.runDialog.allowanceChecking'),
      detail: null,
      canRun: false,
      showUpgrade: false,
    };
  }

  const allowance = decision.customerAllowance;
  if (allowance) {
    // Reservations are unavailable to another request, so the displayed used
    // count reconciles with included - remaining even during an active run.
    const used = Math.max(allowance.limit - allowance.remaining, 0);
    const resetAt = formatResetAt(allowance.resetAt, locale);
    const resetCopy = resetAt ? t('skills.runDialog.allowanceResets', { date: resetAt }) : null;
    return {
      kind: 'allowance',
      title: t('skills.runDialog.allowanceTitle'),
      summary: t('skills.runDialog.allowanceSummary', {
        included: allowance.limit,
        used,
        remaining: allowance.remaining,
      }),
      detail: decision.allowed
        ? resetCopy
        : [decision.message, resetCopy].filter(Boolean).join(' · '),
      canRun: decision.allowed,
      showUpgrade: !decision.allowed && Boolean(decision.recommendedTier),
    };
  }

  if (decision.allowed) {
    return {
      kind: 'included',
      title: t('skills.runDialog.allowanceTitle'),
      summary: t('skills.runDialog.allowanceIncluded'),
      detail: null,
      canRun: true,
      showUpgrade: false,
    };
  }

  return {
    kind: 'blocked',
    title: t('skills.runDialog.allowanceTitle'),
    summary: decision.message || t('skills.runDialog.failed'),
    detail: null,
    canRun: false,
    showUpgrade: Boolean(decision.recommendedTier),
  };
}
